import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiPlus, FiSearch } from 'react-icons/fi';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

// Synthetic event so the component drops into handlers written for native
// <select> elements (they only read target.name / target.value).
export interface SelectChangeEvent {
  target: { name: string; value: string };
}

interface SearchableSelectProps {
  name: string;
  value: string;
  options: SearchableSelectOption[];
  onChange: (event: SelectChangeEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  // Keeps native constraint validation working on submit: an invisible
  // <select required> sits over the trigger so the browser blocks the form
  // and shows its bubble on this field when nothing is selected.
  required?: boolean;
  // Show the "Other — add manually" row (default true).
  allowCustom?: boolean;
  customPlaceholder?: string;
  triggerClassName?: string;
}

const panelInputClassName = `
  w-full rounded-md border border-slate-600/60 bg-slate-800/60 px-3 py-2 text-sm
  text-slate-200 placeholder:text-slate-500 focus:border-emerald-500/50
  focus:outline-none focus:ring-2 focus:ring-emerald-500/40
`;

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  name,
  value,
  options,
  onChange,
  placeholder = 'Select',
  disabled = false,
  required = false,
  allowCustom = true,
  customPlaceholder = 'Enter other value',
  triggerClassName = 'w-full px-4 py-3 bg-slate-800/40 border border-slate-700/50 rounded-lg text-slate-200',
}) => {
  const [open, setOpen] = useState(false);
  const [customMode, setCustomMode] = useState(false);
  const [query, setQuery] = useState('');
  const [customValue, setCustomValue] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const primaryRef = useRef<HTMLInputElement | null>(null);

  const filteredOptions = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term)
    );
  }, [options, query]);

  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const match = options.find((option) => option.value === value);
    return match ? match.label : value;
  }, [value, options]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCustomMode(false);
    setCustomValue('');
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handlePointer = (event: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const commitCustom = () => {
    const trimmed = customValue.trim();
    if (!trimmed) return;
    onChange({ target: { name, value: trimmed } });
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      {required && !disabled && (
        <select
          tabIndex={-1}
          aria-hidden="true"
          value={value}
          onChange={() => {}}
          className="pointer-events-none absolute inset-0 h-full w-full select-none opacity-0"
        >
          <option value="" />
          {value && <option value={value}>{selectedLabel}</option>}
        </select>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((previous) => !previous)}
        className={`${triggerClassName} flex items-center justify-between gap-2 text-left transition-all duration-200 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
          disabled ? 'cursor-not-allowed opacity-60' : ''
        }`}
      >
        <span className={`truncate ${value ? '' : 'text-slate-500'}`}>{selectedLabel || placeholder}</span>
        <FiChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-full overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-2xl shadow-black/50">
          {customMode ? (
            <div className="space-y-3 p-3">
              <input
                ref={primaryRef}
                value={customValue}
                onChange={(event) => setCustomValue(event.target.value)}
                onKeyDown={(event) => {
                  // Inputs inside the form would otherwise submit it on Enter.
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();
                    commitCustom();
                  }
                }}
                placeholder={customPlaceholder}
                autoFocus
                className={panelInputClassName}
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCustomMode(false);
                    setCustomValue('');
                    requestAnimationFrame(() => searchRef.current?.focus());
                  }}
                  className="rounded-md border border-slate-600/60 px-3 py-1.5 text-sm text-slate-300 transition-colors hover:bg-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={commitCustom}
                  disabled={!customValue.trim()}
                  className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Add
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="border-b border-slate-700/50 p-2">
                <div className="relative">
                  <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.preventDefault();
                    }}
                    placeholder="Search options..."
                    className={`${panelInputClassName} pl-9`}
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto">
                {filteredOptions.length === 0 && (
                  <div className="px-4 py-3 text-sm text-slate-500">No matches found</div>
                )}
                {filteredOptions.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      type="button"
                      key={option.value}
                      onClick={() => {
                        onChange({ target: { name, value: option.value } });
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors ${
                        isSelected ? 'bg-emerald-500/10 text-emerald-400' : 'text-slate-300 hover:bg-slate-800'
                      }`}
                    >
                      <span className="truncate">{option.label}</span>
                      {isSelected && <FiCheck className="h-4 w-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {allowCustom && (
                <button
                  type="button"
                  onClick={() => {
                    setCustomMode(true);
                    requestAnimationFrame(() => primaryRef.current?.focus());
                  }}
                  className="flex w-full items-center gap-2 border-t border-slate-700/50 bg-slate-900 px-4 py-2.5 text-left text-sm font-medium text-emerald-400 transition-colors hover:bg-emerald-500/10"
                >
                  <FiPlus className="h-4 w-4" />
                  Other — add manually
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
